import { JsonInput } from 'components/UI/JsonForm/JsonInputRenderer/types';
import { IApplicationFormStep, EFormConfigType } from '../types';
import { chicagoRebuildSteps } from './ChicagoRebuild';
import ChicagoRebuildApplicationDetails from './ChicagoRebuild/ChicagoRebuildApplicationDetails';
import ChicagoRebuildApplications from './ChicagoRebuild/ChicagoRebuildApplications';
import { chicagoRebuildValidationSchema } from './ChicagoRebuild/validationSchema';
import { CRP2022Steps } from './CRP2022';
import { CRP2022ValidationSchema } from './CRP2022/validationSchema';

import { FBDISteps } from './FBDI';
import FBDIApplicationDetails from './FBDI/FBDIApplicationDetails';
import FBDIApplications from './FBDI/FBDIApplications';
import { FBDIValidationSchema } from './FBDI/validationSchema';

import { emailRegEx, phoneRegEx } from 'utils/RegularExpressions';
import * as Yup from 'yup';
import { FBDIDashboard } from './FBDI/FBDIDashboard';
import { FBDIWelcome } from './FBDI/FBDIWelcome';
import { ChicagoRebuildDashboard } from './ChicagoRebuild/ChicagoRebuildDashboard';

import {
  LBAApplicationForm,
  LBAApplicationDetails,
  LBAApplicationsList,
  LBADashboard,
} from './LBA';
import DefaultApplicantInformation from './Components/DefaultApplicantInformation';
import { UserCircleIcon } from '@heroicons/react/outline';
import {
  BFIPApplicationDetails,
  BFIPApplicationForm,
  BFIPApplicationsList,
} from './BFIP';
import {
  BDCDPPApplicationDetails,
  BDCDPPApplicationForm,
  BDCDPPApplicationsList,
  BDCDPPDashboard,
} from './BDCDPP';
import { BFIPDashboard } from './BFIP/BFIPDashboard';
import {
  MDGSApplicationDetails,
  MDGSApplicationForm,
  MDGSApplicationsList,
  MDGSDashboard,
} from './MDGS';
import {
  GStrategiesApplicationForm,
  GStrategiesDashboard,
  GStrategiesApplicationList,
  GStrategiesApplicationDetails,
} from './GStrategies';
import {
  BDPAApplicationDetails,
  BDPAForm,
  BDPAApplicationList,
  BDPADashboard,
} from './BDPA';
import {
  CRApplicationDetails,
  CRApplicationList,
  CRDashboard,
  CRForm,
} from './CR';
import {
  BakerTillyApplicationDetails,
  BakerTillyApplicationForm,
  BakerTillyApplicationsList,
  BakerTillyDashboard,
} from './BakerTilly';
import {
  BTPPApplicationDetails,
  BTPPApplicationForm,
  BTPPApplicationsList,
  BTPPDashboard,
} from './BTPP';
import {
  CHDOApplicationDetails,
  CHDOApplicationForm,
  CHDOApplicationsList,
  CHDODashboard,
} from './CHDO';
import {
  MilwaukeePPApplicationDetails,
  MilwaukeePPApplicationForm,
  MilwaukeePPApplicationsList,
  MilwaukeePPDashboard,
} from './MilwaukeePP';
import {
  MilwaukeeCRApplicationDetails,
  MilwaukeeCRForm,
  MilwaukeeCRApplicationList,
  MilwaukeeCRDashboard,
} from './MilwaukeeCR';
import {
  MDPAApplicationDetails,
  MDPAApplicationList,
  MDPADashboard,
  MDPAForm,
} from './MilwaukeeDPA';
import {
  BHAPPApplicationDetails,
  BHAPPApplicationForm,
  BHAPPApplicationsList,
  BHAPPDashboard,
} from './BHAPP';

export const standardStepSchema = Yup.object({
  applicantInfo: Yup.object({
    fullName: Yup.string()
      .required('Required')
      .max(250, 'Must be less than 250 characters.'),
    contactEmail: Yup.string()
      .required('Required')
      .matches(emailRegEx, 'Please enter a valid email address'),
    contactPhone: Yup.string().required('Required').matches(phoneRegEx, {
      excludeEmptyString: true,
      message: 'Please enter a valid phone number',
    }),
  }),
});

export const applicantInfo: JsonInput[] = [
  {
    type: 'input',
    props: {
      name: 'applicantInfo.fullName',
      label: 'Full Name',
    },
  },
  {
    type: 'input',
    props: {
      name: 'applicantInfo.contactEmail',
      label: 'Contact Email',
    },
  },
  {
    type: 'masked',
    props: {
      name: 'applicantInfo.contactPhone',
      label: 'Contact Phone',
      maskedComponent: 'phone',
    },
  },
];

export const standardSteps: IApplicationFormStep[] = [
  {
    title: 'Applicant Information',
    sections: [
      {
        id: 'applicantInfo',
        title: 'Applicant Information',
        subTitle: 'Please provide information of applicant.',
        inputs: applicantInfo,
      },
    ],
  },
];

export const defaultSteps: {
  name: string;
  sectionId: string;
  Icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  component: React.ReactNode;
}[] = [
  {
    name: 'Applicant Information',
    sectionId: 'applicantInformation',
    Icon: UserCircleIcon,
    component: <DefaultApplicantInformation />,
  },
];

export const formConfigurationMapping = [
  {
    formCode: '0da37648',
    type: EFormConfigType.TEMPLATE,
    formSteps: chicagoRebuildSteps,
    validationSchema: chicagoRebuildValidationSchema,
    applicationDetailsComponent: ChicagoRebuildApplicationDetails,
    applicationsListComponent: ChicagoRebuildApplications,
    applicationDashboardComponent: ChicagoRebuildDashboard,
  },
  {
    formCode: '5430bf5a',
    type: EFormConfigType.TEMPLATE,
    formSteps: CRP2022Steps,
    validationSchema: CRP2022ValidationSchema,
    applicationDetailsComponent: ChicagoRebuildApplicationDetails,
    applicationsListComponent: ChicagoRebuildApplications,
  },
  {
    formCode: '0572bb50',
    type: EFormConfigType.TEMPLATE,
    formSteps: FBDISteps,
    validationSchema: FBDIValidationSchema,
    applicationFormInitialComponent: FBDIWelcome,
    applicationDetailsComponent: FBDIApplicationDetails,
    applicationsListComponent: FBDIApplications,
    applicationDashboardComponent: FBDIDashboard,
  },
  {
    formCode: '729109ff8a1e',
    type: EFormConfigType.FREE,
    applicationFormComponent: LBAApplicationForm,
    applicationDashboardComponent: LBADashboard,
    applicationsListComponent: LBAApplicationsList,
    applicationDetailsComponent: LBAApplicationDetails,
  },
  {
    formCode: '6a28ce0ad072',
    type: EFormConfigType.FREE,
    applicationFormComponent: BFIPApplicationForm,
    applicationDashboardComponent: BFIPDashboard,
    applicationsListComponent: BFIPApplicationsList,
    applicationDetailsComponent: BFIPApplicationDetails,
  },
  {
    formCode: '72dfd04bed9a',
    type: EFormConfigType.FREE,
    applicationFormComponent: BDCDPPApplicationForm,
    applicationDashboardComponent: BDCDPPDashboard,
    applicationsListComponent: BDCDPPApplicationsList,
    applicationDetailsComponent: BDCDPPApplicationDetails,
  },
  {
    formCode: '97d915dcec22',
    type: EFormConfigType.FREE,
    applicationFormComponent: MDGSApplicationForm,
    applicationDashboardComponent: MDGSDashboard,
    applicationsListComponent: MDGSApplicationsList,
    applicationDetailsComponent: MDGSApplicationDetails,
  },
  {
    formCode: 'dfa447a9235b',
    type: EFormConfigType.FREE,
    applicationFormComponent: GStrategiesApplicationForm,
    applicationDashboardComponent: GStrategiesDashboard,
    applicationsListComponent: GStrategiesApplicationList,
    applicationDetailsComponent: GStrategiesApplicationDetails,
  },
  {
    formCode: '38fd852a9f7b',
    type: EFormConfigType.FREE,
    applicationFormComponent: BDPAForm,
    applicationDashboardComponent: BDPADashboard,
    applicationsListComponent: BDPAApplicationList,
    applicationDetailsComponent: BDPAApplicationDetails,
  },
  {
    formCode: 'c66dba6e7785',
    type: EFormConfigType.FREE,
    applicationFormComponent: CRForm,
    applicationDashboardComponent: CRDashboard,
    applicationsListComponent: CRApplicationList,
    applicationDetailsComponent: CRApplicationDetails,
  },
  {
    formCode: '5175302f14de',
    type: EFormConfigType.FREE,
    applicationFormComponent: BakerTillyApplicationForm,
    applicationDashboardComponent: BakerTillyDashboard,
    applicationsListComponent: BakerTillyApplicationsList,
    applicationDetailsComponent: BakerTillyApplicationDetails,
  },
  {
    formCode: '548aa455ede2',
    type: EFormConfigType.FREE,
    applicationFormComponent: BTPPApplicationForm,
    applicationDashboardComponent: BTPPDashboard,
    applicationsListComponent: BTPPApplicationsList,
    applicationDetailsComponent: BTPPApplicationDetails,
  },
  {
    formCode: '1213d0ec977c',
    type: EFormConfigType.FREE,
    applicationFormComponent: CHDOApplicationForm,
    applicationDashboardComponent: CHDODashboard,
    applicationsListComponent: CHDOApplicationsList,
    applicationDetailsComponent: CHDOApplicationDetails,
  },
  {
    formCode: '67b5f545a00a',
    type: EFormConfigType.FREE,
    applicationFormComponent: MilwaukeeCRForm,
    applicationDashboardComponent: MilwaukeeCRDashboard,
    applicationsListComponent: MilwaukeeCRApplicationList,
    applicationDetailsComponent: MilwaukeeCRApplicationDetails,
  },
  {
    formCode: '72dfd545a00a',
    type: EFormConfigType.FREE,
    applicationFormComponent: MilwaukeePPApplicationForm,
    applicationDashboardComponent: MilwaukeePPDashboard,
    applicationsListComponent: MilwaukeePPApplicationsList,
    applicationDetailsComponent: MilwaukeePPApplicationDetails,
  },
  {
    formCode: '38fd854983dw1',
    type: EFormConfigType.FREE,
    applicationFormComponent: MDPAForm,
    applicationDashboardComponent: MDPADashboard,
    applicationsListComponent: MDPAApplicationList,
    applicationDetailsComponent: MDPAApplicationDetails,
  },
  {
    formCode: '804881bc94f7',
    type: EFormConfigType.FREE,
    applicationFormComponent: BHAPPApplicationForm,
    applicationDashboardComponent: BHAPPDashboard,
    applicationsListComponent: BHAPPApplicationsList,
    applicationDetailsComponent: BHAPPApplicationDetails,
  },
];
